import Parse from "parse";
import type { Attributes } from "parse";
import { SECOND_FACTOR_REQUIRED } from "../../shared/secondFactorRequired";
import { InvalidCredentialsError, MfaRequiredError } from "./AuthContext";
import { User } from "./User";
import { resolveActivePersona, resolvePersonas } from "../../shared/persona";
const sessionTokenKey = "sessionToken";

type ParseUser<T extends Attributes = Attributes> = InstanceType<
  typeof Parse.User<T>
>;

Parse.serverURL =
  process.env.NODE_ENV === "production"
    ? "/parse"
    : "http://localhost:1337/parse";
console.log("NODE_ENV=" + process.env.NODE_ENV);
Parse.initialize("casesolv", "casesolv");

const feedToken = (consumer: (token: string) => void) => {
  const localStorageToken = localStorage.getItem(sessionTokenKey);
  if (localStorageToken) {
    console.debug("Got session token from localStorage");
    consumer(localStorageToken);
    return;
  }
  const sessionStorageToken = sessionStorage.getItem(sessionTokenKey);
  if (sessionStorageToken) {
    console.debug("Got session token from sessionStorage");
    consumer(sessionStorageToken);
    return;
  }
  console.log("No session token found");
};

function saveInSessionStorage(token: string): void {
  sessionStorage.setItem(sessionTokenKey, token);
  localStorage.removeItem(sessionTokenKey);
}

function saveInLocalStorage(token: string): void {
  localStorage.setItem(sessionTokenKey, token);
  sessionStorage.removeItem(sessionTokenKey);
}

function deleteToken() {
  localStorage.removeItem(sessionTokenKey);
  sessionStorage.removeItem(sessionTokenKey);
}

function hasMfaEnrolment(parseUser: ParseUser<Attributes>): boolean {
  // True if the local user object hints at any MFA state. The canonical
  // afterFind-processed form is { status: "enabled" }, but the post-save echo
  // that the Parse SDK persists to localStorage carries { secret, recovery }
  // instead — we treat both as "enrolment exists on the server."
  const mfa = parseUser.get("authData")?.mfa;
  if (!mfa) return false;
  return mfa.status === "enabled" || !!mfa.secret || !!mfa.mobile;
}

function isMfaEnabled(parseUser: ParseUser<Attributes>): boolean {
  // Enrolment is two-phase: the secret being stored only means MFA is "pending",
  // it isn't fully active until the user confirms they've saved the recovery codes.
  return hasMfaEnrolment(parseUser) && !!parseUser.get("mfaConfirmedAt");
}

function asUser(parseUser: ParseUser<Attributes>): User {
  const personas = resolvePersonas(parseUser.get("personas"));
  return {
    username: parseUser.getUsername() || "Unknown",
    email: parseUser.get("email"),
    lastName: parseUser.get("lastName"),
    firstName: parseUser.get("firstName"),
    currencyCode: parseUser.get("currencyCode"),
    personas,
    activePersona: resolveActivePersona(
      personas,
      parseUser.get("activePersona"),
    ),
    mfaEnabled: isMfaEnabled(parseUser),
    mfaConfirmedAt: parseUser.get("mfaConfirmedAt"),
  };
}

function isMfaTokenRequiredError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = (error as { code?: number }).code;
  const message = (error as { message?: string }).message ?? "";
  // parse-server 9.x signals a missing additional auth provider with
  // OTHER_CAUSE + a message naming the provider, not MFA_TOKEN_REQUIRED.
  return (
    code === Parse.Error.MFA_TOKEN_REQUIRED ||
    (code === Parse.Error.OTHER_CAUSE &&
      /Missing additional authData\b.*\bmfa\b/.test(message))
  );
}

function persistToken(token: string | null, remember: boolean) {
  if (token === null) {
    console.error("Session token missing");
    return;
  }
  if (remember) saveInLocalStorage(token);
  else saveInSessionStorage(token);
}

class ParseAuth {
  private onAuthenticated: ((user: User) => void) | undefined = undefined;
  private onSessionExpired: (() => void) | undefined = undefined;
  // The session whose expiry notifySessionExpired() has handled.
  // (Agent-written comment.)
  private expiredSessionToken: string | undefined = undefined;

  setCallback(newOnAuthenticated: (user: User) => void): () => void {
    this.onAuthenticated = newOnAuthenticated;
    return () => {
      if (this.onAuthenticated === newOnAuthenticated)
        this.onAuthenticated = undefined;
    };
  }

  setSessionExpiredCallback(newOnSessionExpired: () => void): () => void {
    this.onSessionExpired = newOnSessionExpired;
    return () => {
      if (this.onSessionExpired === newOnSessionExpired)
        this.onSessionExpired = undefined;
    };
  }

  private notify(parseUser: ParseUser<Attributes>, overrides?: Partial<User>) {
    if (!this.onAuthenticated) return;
    this.onAuthenticated({ ...asUser(parseUser), ...overrides });
  }

  // The server told us the session held in storage no longer exists (it
  // expired, or was revoked). Drop it, and the SDK's own cached current user
  // with it, so a later login() does not mistake the stale cache for a valid
  // session; then let the sign-in page take over.
  // The SDK drops its cached user only once the promise chain of logOut()
  // runs. Until then other requests refused at the same time call this again
  // for the same session, and logOut()'s own request to the server can be
  // refused too; remembering the session handled makes those calls no-ops,
  // while the expiry of a session signed in later is still reported.
  // (Agent-written comment.)
  notifySessionExpired(): void {
    const sessionToken = this.getToken();
    if (sessionToken === undefined || sessionToken === this.expiredSessionToken)
      return;
    this.expiredSessionToken = sessionToken;
    deleteToken();
    Parse.User.logOut().catch(() => {});
    this.onSessionExpired?.();
  }

  async checkSession() {
    const currentUser = Parse.User.current();
    if (currentUser !== null && currentUser !== undefined) {
      console.log("Already authenticated");
      this.notify(currentUser);
      await this.reconcileUnconfirmedMfa(currentUser);
      return;
    }
    return feedToken((token) =>
      Parse.User.become(token)
        .then(async (user) => {
          console.log("Logged in to existing session");
          this.notify(user);
          await this.reconcileUnconfirmedMfa(user);
        })
        .catch((error) => {
          console.log("Failed to log in using old session", error);
          deleteToken();
        }),
    );
  }

  // If the server has stored MFA data but the user never acknowledged the
  // recovery codes, roll the enrolment back so they can start over.
  private async reconcileUnconfirmedMfa(
    parseUser: ParseUser<Attributes>,
  ): Promise<void> {
    if (!hasMfaEnrolment(parseUser)) return;
    if (parseUser.get("mfaConfirmedAt")) return;
    try {
      await this.clearUnconfirmedMfa();
    } catch (err) {
      console.warn("Failed to clear unconfirmed MFA on bootstrap", err);
    }
  }

  async login(
    username: string,
    password: string,
    remember = false,
  ): Promise<void> {
    if (this.isAuthenticated()) {
      return;
    }
    console.log("Logging in");
    try {
      const loggedInUser = await Parse.User.logIn(username, password);
      persistToken(loggedInUser.getSessionToken(), remember);
      console.log("Signed in user " + loggedInUser.get("username"));
      this.notify(loggedInUser, { username });
    } catch (error) {
      if (isMfaTokenRequiredError(error)) {
        throw new MfaRequiredError(username, password, remember);
      }
      // parse-server answers a wrong username or password with
      // OBJECT_NOT_FOUND ("Invalid username/password.")
      if ((error as { code?: number })?.code === Parse.Error.OBJECT_NOT_FOUND) {
        throw new InvalidCredentialsError();
      }
      throw error;
    }
  }

  async loginWithMfa(
    username: string,
    password: string,
    token: string,
    remember = false,
  ): Promise<void> {
    if (this.isAuthenticated()) {
      return;
    }
    console.log("Logging in with MFA");
    const loggedInUser = await Parse.User.logInWithAdditionalAuth(
      username,
      password,
      { mfa: { token } },
    );
    persistToken(loggedInUser.getSessionToken(), remember);
    console.log("Signed in user " + loggedInUser.get("username"));
    this.notify(loggedInUser, { username });
  }

  async logout(): Promise<void> {
    if (!this.isAuthenticated()) {
      return;
    }
    deleteToken();
    console.log("Logging out");
    await Parse.User.logOut()
      .then(() => console.log("Logged out"))
      .catch((error) => console.log("Failed to log out", error));
  }

  isAuthenticated() {
    return Parse.User.current() !== null;
  }

  getToken(): string | undefined {
    return Parse.User.current()?.getSessionToken() ?? undefined;
  }

  async updateUserField(key: string, value: string): Promise<void> {
    const user = Parse.User.current();
    if (!user) throw new Error("Not logged in");
    user.set(key, value);
    const saved = await user.save();
    this.notify(saved);
  }

  // Changes the password of the logged-in user. The server ends all of the
  // user's sessions with it and answers with a new one for this browser, which
  // the SDK takes over.
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    const user = Parse.User.current();
    if (!user) throw new Error("Not logged in");
    user.setPassword(newPassword);
    try {
      // the server takes the current password from the context of the save
      // (server/src/cloud/passwordChange.ts)
      await user.save(null, { context: { password: currentPassword } });
    } catch (error) {
      // a later save of the user must not carry the refused password along
      user.revert("password");
      throw error;
    }
    // the token kept for the next visit stays where the login put it
    persistToken(
      user.getSessionToken(),
      localStorage.getItem(sessionTokenKey) !== null,
    );
    this.notify(user);
  }

  async enrolMfa(
    secret: string,
    token: string,
  ): Promise<{ recoveryCodes: string[] }> {
    const user = Parse.User.current();
    if (!user) throw new Error("Not logged in");
    const authData = user.get("authData") || {};
    user.set("authData", { ...authData, mfa: { secret, token } });
    const saved = await user.save();
    const response = saved.get("authDataResponse") || {};
    const recoveryString: string = response.mfa?.recovery || "";
    const recoveryCodes = recoveryString
      .split(/,\s*/)
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    this.notify(saved);
    return { recoveryCodes };
  }

  // Reads the user anew from the server, whatever the browser has cached.
  async rereadUser(): Promise<void> {
    const user = Parse.User.current();
    if (!user) return;
    await user.fetch();
    this.notify(user);
  }

  async confirmMfa(): Promise<void> {
    await Parse.Cloud.run("confirmMfa");
    // Refetch so the SDK's cached user picks up mfaConfirmedAt and the
    // afterFind-processed authData.mfa = { status: "enabled" } shape.
    await this.rereadUser();
  }

  async clearUnconfirmedMfa(): Promise<boolean> {
    const { cleared } = await Parse.Cloud.run("clearUnconfirmedMfa");
    if (!cleared) return false;
    await this.rereadUser();
    return true;
  }

  // The server checks the code against the second factor and removes it
  // (server/src/cloud/secondFactor.ts); the user read anew has none.
  async disableMfa(currentToken: string): Promise<void> {
    await Parse.Cloud.run("resetSecondFactor", { code: currentToken });
    await this.rereadUser();
  }
}

const auth = new ParseAuth();

// The server refuses the data requests of a user without a second factor (see
// server/src/cloud/secondFactor.ts). The user cached in the browser can say
// they have one all the same, e.g. once it was removed on the server, so that
// refusal re-reads them, which brings up the enrolment screen. Every request
// the SDK makes passes through its REST controller.
const restController = Parse.CoreManager.getRESTController();
Parse.CoreManager.setRESTController({
  ...restController,
  request: (...args: Parameters<typeof restController.request>) =>
    restController.request(...args).catch((error: { code?: number }) => {
      if (error?.code === SECOND_FACTOR_REQUIRED) {
        auth
          .rereadUser()
          .catch((err) => console.warn("Failed to re-read the user", err));
      } else if (error?.code === Parse.Error.INVALID_SESSION_TOKEN) {
        auth.notifySessionExpired();
      }
      throw error;
    }),
});

export default auth;
